import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Autocomplete,
  Box,
  CircularProgress,
  ClickAwayListener,
  Fade,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from 'es-toolkit/compat';
import { LngLatBoundsLike, MapRef } from 'react-map-gl/mapbox';

import { MapboxFeature } from '../../types';

import { useLazyGetMapSearchQuery } from '@/api/endpoints/offlineSalesOptimizer';
import { SEARCH_DEBOUNCE_TIME_MS } from '@/app/constants';
import { SquareIconButton } from '@/shared/components/iconButton';
import { Search, X } from '@/shared/components/icons';

interface MapSearchControlProps {
  mapRef: MapRef | null;
  mapboxAccessToken: string;
}

const MapSearchControl = ({
  mapRef,
  mapboxAccessToken,
}: MapSearchControlProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [getMapSearch, { data: searchResults, isFetching }] =
    useLazyGetMapSearchQuery();

  const toggleOpen = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const close = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 10);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [open]);

  const handleSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.trim()) {
          const center = mapRef?.getCenter();
          const proximity = center
            ? `${center.lng.toString()},${center.lat.toString()}`
            : undefined;

          void getMapSearch({
            searchText: value,
            accessToken: mapboxAccessToken,
            language: 'en-US',
            proximity,
          });
        }
      }, SEARCH_DEBOUNCE_TIME_MS),
    [getMapSearch, mapRef, mapboxAccessToken]
  );

  const handleOptionSelect = (option: MapboxFeature | null) => {
    if (option && mapRef) {
      if (option.bbox) {
        mapRef.fitBounds(option.bbox as LngLatBoundsLike, {
          padding: 50,
          essential: true,
        });
      } else {
        const [longitude, latitude] = option.center;
        mapRef.flyTo({
          center: [longitude, latitude],
          zoom: 10,
          essential: true,
        });
      }
      close();
    }
  };

  return (
    <ClickAwayListener onClickAway={close}>
      <Box ref={anchorRef} position='relative'>
        <Fade in={open}>
          <Paper
            elevation={0}
            sx={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              mb: 0.5,
            }}
          >
            <Autocomplete
              size='small'
              onChange={(_event, option) => {
                handleOptionSelect(option);
              }}
              onInputChange={(_event, value, reason) => {
                if (reason === 'input') {
                  handleSearch(value);
                }
              }}
              loading={isFetching}
              loadingText='Searching...'
              noOptionsText='No locations'
              options={searchResults?.features ?? []}
              getOptionLabel={(option) => option.place_name}
              filterOptions={(options) => options}
              slotProps={{
                popper: {
                  disablePortal: true,
                },
              }}
              renderOption={(
                props: React.HTMLAttributes<HTMLLIElement>,
                option
              ) => {
                const { key, ...otherProps } = props as {
                  key: React.Key;
                } & React.HTMLAttributes<HTMLLIElement>;

                const optionTexts = option.place_name.split(', ');
                const mainText = optionTexts[0];
                const subText = optionTexts.slice(1).join(', ');

                return (
                  <li key={key} {...otherProps} style={{ display: 'block' }}>
                    <Typography variant='body1' color='text.tertiary'>
                      {mainText}
                    </Typography>
                    <Typography
                      variant='body3'
                      component='p'
                      color='text.secondary'
                    >
                      {subText}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  aria-label='Search location'
                  {...params}
                  inputRef={inputRef}
                  placeholder='Search'
                  sx={{ width: 240 }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search color='inherit' fontSize='small' />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {isFetching ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </Paper>
        </Fade>

        <SquareIconButton
          size='small'
          color='inherit'
          aria-label='toggle search location'
          onClick={toggleOpen}
        >
          {open ? <X fontSize='small' /> : <Search fontSize='small' />}
        </SquareIconButton>
      </Box>
    </ClickAwayListener>
  );
};

export default MapSearchControl;
